/**
 * Removes trailing punctuation and unmatched closing brackets from a URL.
 * @remarks Migration: New helper; the original regex included trailing punctuation in links.
 * @param url - URL candidate found in a message.
 * @returns URL with surrounding punctuation removed and balanced brackets preserved.
 */
function trimUrlPunctuation(url: string): string {
  const openingBrackets: Record<string, string> = { ")": "(", "]": "[", "}": "{" };

  while (url.length > 0) {
    const lastCharacter = url[url.length - 1];
    const openingBracket = openingBrackets[lastCharacter];
    const hasExtraClosingBracket = openingBracket
      && url.split(lastCharacter).length > url.split(openingBracket).length;

    if (!/[.,!?;:…’”»]/.test(lastCharacter) && !hasExtraClosingBracket) {
      break;
    }

    url = url.slice(0, -1);
  }

  return url;
}

/**
 * Converts plain HTTP(S) URLs in an HTML message into links.
 * @remarks Migration: Traverses text nodes instead of replacing HTML with a regex,
 * preserving existing anchors and avoiding truncated URLs or nested links on repeated calls.
 * This function preserves existing HTML; it is not a sanitizer.
 * @param message - Status update message that may contain HTML.
 * @returns Message with clickable URLs, or the original string when no links were added.
 */
export function linkifyMessage(message: string): string {
  const template = document.createElement("template");
  template.innerHTML = message;

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT, {
    /**
     * Selects text outside existing anchors and elements that must not contain generated links.
     * @remarks Migration: Replaces the original regex lookbehind checks with DOM ancestry checks.
     * @param node - Text node being visited.
     * @returns Whether the walker should include the node for URL conversion.
     */
    acceptNode(node) {
      return node.parentElement?.closest("a, script, style, textarea, title")
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT;
    },
  });
  const textNodes: Text[] = [];

  // Collect first so replacing nodes cannot interrupt traversal or revisit new links.
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(node as Text);
  }

  let changed = false;
  for (const node of textNodes) {
    const text = node.data;
    const fragment = document.createDocumentFragment();
    let offset = 0;

    for (const match of text.matchAll(/https?:\/\/[^\s<>"']+/g)) {
      const url = trimUrlPunctuation(match[0]);
      if (url.endsWith("://")) {
        continue;
      }

      const anchor = document.createElement("a");
      anchor.setAttribute("href", url);
      anchor.setAttribute("target", "_blank");
      anchor.textContent = url;
      fragment.append(text.slice(offset, match.index), anchor, match[0].slice(url.length));
      offset = match.index + match[0].length;
    }

    if (offset > 0) {
      fragment.append(text.slice(offset));
      node.replaceWith(fragment);
      changed = true;
    }
  }

  return changed ? template.innerHTML : message;
}
